import React, { useState, useEffect, useRef } from 'react';

import logo from '../../assets/logo.png';
import drop from '../../assets/icons/dropdown.svg';
import { useAuth } from '../context/AuthContext';
import { userDashboardAPI } from '../../services/api';
import cricketService from '../../services/cricketAPI';
import bettingService from '../../services/bettingAPI';
import CurrentBets from './CurrentBets';
import AccountStatement from './AccountStatement';
import ChangePassword from './ChangePassword';
import MatchCard from '../cricket/MatchCard';
import toast from 'react-hot-toast';
import '../../styles/user/UserDashboard.css';

import '../../fonts/dfont.eot';
import '../../fonts/dfont.ttf';
import '../../fonts/dfont.woff';

import { useNavigate } from 'react-router-dom';

const UserDashboard = () => {
  
    
    const [activeDropdown, setActiveDropdown] = useState(false);
    const [activeModal, setActiveModal] = useState(null); // 'history', 'bets', 'password'
    const [matches, setMatches] = useState([]);
    const [filteredMatches, setFilteredMatches] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [lastUpdated, setLastUpdated] = useState(null);
    
    // Local user stats state
    const [userStats, setUserStats] = useState({
        balance: 0,
        exposure: 0,
        availableBalance: 0
    });
    const [statsLoading, setStatsLoading] = useState(false);

    const showLocked = () => {
    toast.error("User is locked");
};
    
    const { user, logout } = useAuth();
    const navigate = useNavigate();
    const desktopDropdownRef = useRef(null);
    const mobileDropdownRef = useRef(null);
    const statsIntervalRef = useRef(null);
    const [currentTime, setCurrentTime] = useState(new Date());

    // Format currency
    const formatCurrency = (amount) => {
        if (!amount && amount !== 0) return '₹0';
        return `₹${amount.toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;
    };

    // Update clock every second
    useEffect(() => {
        const timer = setInterval(() => {
            setCurrentTime(new Date());
        }, 1000);
        return () => clearInterval(timer);
    }, []);

    // Fetch user stats every 2 seconds
    const fetchUserStats = async () => {
        try {
            setStatsLoading(true);
            
            console.log('1️⃣ Fetching dashboard stats...');
            const statsResponse = await userDashboardAPI.getDashboardStats();
            console.log('2️⃣ Full Dashboard Stats Response:', JSON.stringify(statsResponse, null, 2));
            console.log('3️⃣ statsResponse.data:', statsResponse.data);
            
            console.log('4️⃣ Fetching exposure...');
            const exposureResponse = await bettingService.getUserExposure();
            console.log('5️⃣ Exposure Response:', exposureResponse);
            
            // Extract balance from stats API
            let balance = 0;
            
            // Check different possible response structures
            if (statsResponse?.data) {
                console.log('6️⃣ statsResponse.data keys:', Object.keys(statsResponse.data));
                
                // Check if balance is directly in data
                if (statsResponse.data.balance !== undefined) {
                    balance = parseFloat(statsResponse.data.balance);
                    console.log('7️⃣ Balance found in data.balance:', balance);
                }
                // Check if balance is in data.data
                else if (statsResponse.data.data?.balance !== undefined) {
                    balance = parseFloat(statsResponse.data.data.balance);
                    console.log('7️⃣ Balance found in data.data.balance:', balance);
                }
                // Check if balance is in data.user
                else if (statsResponse.data.user?.balance !== undefined) {
                    balance = parseFloat(statsResponse.data.user.balance);
                    console.log('7️⃣ Balance found in data.user.balance:', balance);
                }
                else {
                    console.log('7️⃣ Balance not found in any expected location');
                    console.log('Available data:', statsResponse.data);
                }
            }
            
            // Extract exposure
            let exposure = 0;
            if (exposureResponse?.data) {
                if (exposureResponse.data.totalExposure !== undefined) {
                    exposure = parseFloat(exposureResponse.data.totalExposure);
                } else if (exposureResponse.data.exposure !== undefined) {
                    exposure = parseFloat(exposureResponse.data.exposure);
                }
                console.log('8️⃣ Exposure extracted:', exposure);
            }
            
            console.log('9️⃣ Setting user stats:', { balance, exposure, available: balance - exposure });
            
            setUserStats({
                balance,
                exposure,
                availableBalance: balance - exposure
            });
            
        } catch (error) {
            console.error('❌ Error fetching user stats:', error);
            console.error('Error details:', error.response?.data || error.message);
            // Fallback to auth context
            if (user?.balance !== undefined) {
                const balance = parseFloat(user.balance) || 0;
                setUserStats({
                    balance,
                    exposure: 0,
                    availableBalance: balance
                });
            }
        } finally {
            setStatsLoading(false);
        }
    };

    // Close dropdown when clicking outside
useEffect(() => {
    const handleClickOutside = (event) => {
        const clickedInsideDesktop =
            desktopDropdownRef.current?.contains(event.target);

        const clickedInsideMobile =
            mobileDropdownRef.current?.contains(event.target);

        if (!clickedInsideDesktop && !clickedInsideMobile) {
            setActiveDropdown(false);
        }
    };

    document.addEventListener('mousedown', handleClickOutside);

    return () => {
        document.removeEventListener('mousedown', handleClickOutside);
    };
}, []);

    // Fetch stats on mount and set up interval
    useEffect(() => {
        fetchUserStats();
        
        // Set up interval for every 2 seconds
        statsIntervalRef.current = setInterval(fetchUserStats, 2000);
        
        return () => {
            if (statsIntervalRef.current) {
                clearInterval(statsIntervalRef.current);
            }
        };
    }, [user]);

    // Fetch cricket matches ONLY ONCE when page opens
    useEffect(() => {
        fetchMatches();
        checkAPIStatus(); // Optional: check API health
        
        // NO auto-refresh interval - only fetch once
        // const interval = setInterval(fetchMatches, 30000); // REMOVED
        // return () => clearInterval(interval); // REMOVED
        
    }, []); // Empty dependency array ensures this runs only once on mount

    const fetchMatches = async () => {
        try {
            setLoading(true);
            // Use the new method
            const data = await cricketService.getAllMatches();
            setMatches(data.sport.body);
            setFilteredMatches(data.sport.body); // Set filtered matches to all matches
            setLastUpdated(new Date());
            setError(null);
        } catch (err) {
            console.error('Fetch error:', err);
            setError('Failed to fetch live cricket matches. Please try again later.');
        } finally {
            setLoading(false);
        }
    };

    const checkAPIStatus = async () => {
        try {
            const status = await cricketService.getStatus();
            console.log('Cricket API Status:', status);
        } catch (err) {
            console.error('Status check failed:', err);
        }
    };

    const handleLogout = async () => {
        try {
            await userDashboardAPI.logout();
            logout();
            navigate('/login');
        } catch (error) {
            console.error('Logout error:', error);
            logout();
            navigate('/login');
        }
    };

    const formatNumber = (num) => {
        if (!num && num !== 0) return '0';
        return num.toLocaleString('en-IN');
    };

    const formatDateTime = (dateString) => {
        const date = new Date(dateString);
        return date.toLocaleString('en-IN', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    // Manual refresh function - user can click refresh button
    const handleManualRefresh = async () => {
        await fetchMatches();
    };

    if (!user || user.role !== 'user') {
        return <div className="loading">Access denied. User only area.</div>;
    }

    return (
     <div className="user-dashboard">
    {/* Scrollable Header with Site Name on Left and Username with Dropdown on Right */}


      <header className="ce-header">
        
        {/* MOBILE HAMBURGER (Left) - Only shows on mobile via CSS */}
        <button className="mobile-hamburger" onClick={() => {
            const toggle = document.getElementById('sidebar-toggle');
            if(toggle) toggle.checked = !toggle.checked;
        }}>
            ☰
        </button>

        {/* LOGO (Center-Left) */}
        <div className="ce-logo-wrap">
            <a href="#" onClick={(e) => { e.preventDefault(); navigate('/user/dashboard'); }}>
                <img src={logo} alt="Wazirbook" className="ce-logo" />
            </a>
        </div>

                <div className="ce-user-details desktop-only">
                    <div className="ce-user-balance">
                        <div className="ce-balance-item"><span>Balance:</span><b>{formatCurrency(userStats.balance)}</b></div>
                        <div className="ce-balance-item"><span>Exp:</span><b>{formatCurrency(userStats.exposure)}</b></div>
                    </div>
                    <div className="ce-user" ref={desktopDropdownRef}>
                        <div className="ce-user-name" onClick={() => setActiveDropdown(!activeDropdown)}>
                            <span>{user?.username}</span>
                            <img src={drop} alt="dropdown" className="ce-dropdown-icon" />
                        </div>
                        {activeDropdown && (
                            <div className="ce-dropdown">
                                <button onClick={() => { setActiveDropdown(false); navigate('/user/account-statement'); }}>Account History</button>
                                <button onClick={() => { setActiveDropdown(false); navigate('/user/current-bets'); }}>Current Bets</button>
                                <button onClick={() => { setActiveDropdown(false); navigate('/user/change-password'); }}>Change Password</button>
                                <button className="ce-logout" onClick={handleLogout}>Logout</button>
                            </div>
                        )}
                    </div>
                </div>

                {/* =========================================================== */}
                {/* MOBILE USER DETAILS - BRAND NEW STRUCTURE                  */}
                {/* =========================================================== */}
                <div className="ce-user-details mobile-only">
                    {/* Row 1: Balance (Top) */}
                    <div className="mobile-balance-row">
                        <span>Balance:</span> <b>{formatCurrency(userStats.balance)}</b>
                    </div>
                    
                    {/* Row 2: Exposure + Username (Side by Side exactly as you asked) */}
                    <div className="mobile-exposure-user-row">
                        <div className="mobile-exp-block">
                            <span>Exp:</span> <b>{formatCurrency(userStats.exposure)}</b>
                        </div>
                        <div className="mobile-user-block" ref={mobileDropdownRef}>
    <div
        className="ce-user-name"
        onClick={() => setActiveDropdown(prev => !prev)}
    >
        <span>{user?.username}</span>
        <img
            src={drop}
            alt="dropdown"
            className="ce-dropdown-icon"
        />
    </div>

    {activeDropdown && (
        <div className="ce-dropdown">
            <button
                type="button"
                onClick={() => {
                    setActiveDropdown(false);
                    navigate('/user/account-statement');
                }}
            >
                Account History
            </button>

            <button
                type="button"
                onClick={() => {
                    setActiveDropdown(false);
                    navigate('/user/current-bets');
                }}
            >
                Current Bets
            </button>

            <button
                type="button"
                onClick={() => {
                    setActiveDropdown(false);
                    navigate('/user/change-password');
                }}
            >
                Change Password
            </button>

            <button
                type="button"
                className="ce-logout"
                onClick={handleLogout}
            >
                Logout
            </button>
        </div>
    )}
</div>
                    </div>
                </div>

    </header>

            <ul className="nav nav-tabs mobile-menu-tabs">

    {/* Crash */}
    <li className="nav-item">
        <a
            className="nav-link"
            href="#"
            onClick={(e) => {
                e.preventDefault();
                showLocked();
            }}
        >
            <svg
                id="Layer_2"
                data-name="Layer 2"
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 457.6 277.4"
            >
                {/* KEEP YOUR EXISTING SVG PATHS HERE */}
            </svg>
            <span className="ms-1">Crash</span>
        </a>
    </li>

    {/* Lottery */}
    <li className="nav-item">
        <a
            className="nav-link"
            href="#"
            onClick={(e) => {
                e.preventDefault();
                showLocked();
            }}
        >
            <span>Lottery</span>
        </a>
    </li>

    {/* Sports */}
    <li className="nav-item">
        <a
            className="nav-link active"
            href="#"
            onClick={(e) => {
                e.preventDefault();
                showLocked();
            }}
        >
            <span>Sports</span>
        </a>
    </li>

    {/* Our Casino */}
    <li className="nav-item">
        <a
            className="nav-link"
            href="#"
            onClick={(e) => {
                e.preventDefault();
                showLocked();
            }}
        >
            <span>Our Casino</span>
        </a>
    </li>

    {/* Live Casino */}
    <li className="nav-item">
        <a
            className="nav-link"
            href="#"
            onClick={(e) => {
                e.preventDefault();
                showLocked();
            }}
        >
            <span>Live Casino</span>
        </a>
    </li>

    {/* Slots */}
    <li className="nav-item">
        <a
            className="nav-link"
            href="#"
            onClick={(e) => {
                e.preventDefault();
                showLocked();
            }}
        >
            <span>Slots</span>
        </a>
    </li>

    {/* Fantasy */}
    <li className="nav-item">
        <a
            className="nav-link"
            href="#"
            onClick={(e) => {
                e.preventDefault();
                showLocked();
            }}
        >
            <span>Fantasy</span>
        </a>
    </li>

</ul>

            {/* ============================================== */}
            {/* MOBILE ONLY GREY SPORTS TABS (New Insertion)   */}
            {/* ============================================== */}
<ul className="nav nav-pills sports-tab mobile-sports-tabs">
    {/* Cricket - working */}
    <li className="nav-item">
        <a
            className="nav-link active"
            onClick={() => navigate('/user/dashboard')}
        >
            <div className="d-xl-none">
                <i className="icon icon-4"></i>
            </div>
            <span>Cricket</span>
        </a>
    </li>

    {/* Football */}
    <li className="nav-item">
        <a className="nav-link" onClick={showLocked}>
            <div className="d-xl-none"><i className="icon icon-1"></i></div>
            <span>Football</span>
        </a>
    </li>

    {/* Tennis */}
    <li className="nav-item">
        <a className="nav-link" onClick={showLocked}>
            <div className="d-xl-none"><i className="icon icon-2"></i></div>
            <span>Tennis</span>
        </a>
    </li>

    {/* Table Tennis */}
    <li className="nav-item">
        <a className="nav-link" onClick={showLocked}>
            <div className="d-xl-none"><i className="icon icon-8"></i></div>
            <span>Table Tennis</span>
        </a>
    </li>

    {/* Horse Racing */}
    <li className="nav-item">
        <a className="nav-link" onClick={showLocked}>
            <div className="d-xl-none"><i className="icon icon-10"></i></div>
            <span>Horse Racing</span>
        </a>
    </li>

    {/* Greyhound Racing */}
    <li className="nav-item">
        <a className="nav-link" onClick={showLocked}>
            <div className="d-xl-none"><i className="icon icon-65"></i></div>
            <span>Greyhound Racing</span>
        </a>
    </li>

    {/* Basketball */}
    <li className="nav-item">
        <a className="nav-link" onClick={showLocked}>
            <div className="d-xl-none"><i className="icon icon-15"></i></div>
            <span>Basketball</span>
        </a>
    </li>

    {/* Esoccer */}
    <li className="nav-item">
        <a className="nav-link" onClick={showLocked}>
            <div className="d-xl-none"><i className="icon icon-68"></i></div>
            <span>Esoccer</span>
        </a>
    </li>

    {/* Wrestling */}
    <li className="nav-item">
        <a className="nav-link" onClick={showLocked}>
            <div className="d-xl-none"><i className="icon icon-69"></i></div>
            <span>Wrestling</span>
        </a>
    </li>

    {/* Volleyball */}
    <li className="nav-item">
        <a className="nav-link" onClick={showLocked}>
            <div className="d-xl-none"><i className="icon icon-18"></i></div>
            <span>Volleyball</span>
        </a>
    </li>

    {/* Badminton */}
    <li className="nav-item">
        <a className="nav-link" onClick={showLocked}>
            <div className="d-xl-none"><i className="icon icon-22"></i></div>
            <span>Badminton</span>
        </a>
    </li>

    {/* Snooker */}
    <li className="nav-item">
        <a className="nav-link" onClick={showLocked}>
            <div className="d-xl-none"><i className="icon icon-59"></i></div>
            <span>Snooker</span>
        </a>
    </li>

    {/* Darts */}
    <li className="nav-item">
        <a className="nav-link" onClick={showLocked}>
            <div className="d-xl-none"><i className="icon icon-57"></i></div>
            <span>Darts</span>
        </a>
    </li>

    {/* Boxing */}
    <li className="nav-item">
        <a className="nav-link" onClick={showLocked}>
            <div className="d-xl-none"><i className="icon icon-6"></i></div>
            <span>Boxing</span>
        </a>
    </li>

    {/* Mixed Martial Arts */}
    <li className="nav-item">
        <a className="nav-link" onClick={showLocked}>
            <div className="d-xl-none"><i className="icon icon-3"></i></div>
            <span>Mixed Martial Arts</span>
        </a>
    </li>

    {/* American Football */}
    <li className="nav-item">
        <a className="nav-link" onClick={showLocked}>
            <div className="d-xl-none"><i className="icon icon-58"></i></div>
            <span>American Football</span>
        </a>
    </li>

    {/* E Games */}
    <li className="nav-item">
        <a className="nav-link" onClick={showLocked}>
            <div className="d-xl-none"><i className="icon icon-11"></i></div>
            <span>E Games</span>
        </a>
    </li>

    {/* Ice Hockey */}
    <li className="nav-item">
        <a className="nav-link" onClick={showLocked}>
            <div className="d-xl-none"><i className="icon icon-19"></i></div>
            <span>Ice Hockey</span>
        </a>
    </li>

    {/* Futsal */}
    <li className="nav-item">
        <a className="nav-link" onClick={showLocked}>
            <div className="d-xl-none"><i className="icon icon-9"></i></div>
            <span>Futsal</span>
        </a>
    </li>

    {/* Motor Sports */}
    <li className="nav-item">
        <a className="nav-link" onClick={showLocked}>
            <div className="d-xl-none"><i className="icon icon-52"></i></div>
            <span>Motor Sports</span>
        </a>
    </li>

    {/* Politics */}
    <li className="nav-item">
        <a className="nav-link" onClick={showLocked}>
            <div className="d-xl-none"><i className="icon icon-40"></i></div>
            <span>Politics</span>
        </a>
    </li>

    {/* Kabaddi */}
    <li className="nav-item">
        <a className="nav-link" onClick={showLocked}>
            <div className="d-xl-none"><i className="icon icon-66"></i></div>
            <span>Kabaddi</span>
        </a>
    </li>
</ul>

    

    

    <div className="header-bottom">
<nav className="navbar navbar-expand">

    
    <ul className="navbar-nav">

        <li className="nav-item">
            <span className="nav-link" onClick={() => toast.error("Game Locked")}>
                Home
            </span>
        </li>

        <li className="nav-item">
            <span className="nav-link" onClick={() => toast.error("Game Locked")}>
                Lottery
            </span>
        </li>

        <li className="nav-item">
            <span
                className="nav-link active"
                onClick={() => navigate("/user/dashboard")}
            >
                Cricket
            </span>
        </li>

        <li className="nav-item">
            <span className="nav-link" onClick={() => toast.error("Game Locked")}>
                Tennis
            </span>
        </li>

        <li className="nav-item">
            <span className="nav-link" onClick={() => toast.error("Game Locked")}>
                Football
            </span>
        </li>

        <li className="nav-item">
            <span className="nav-link" onClick={() => toast.error("Game Locked")}>
                Table Tennis
            </span>
        </li>

        <li className="nav-item">
            <span className="nav-link" onClick={() => toast.error("Game Locked")}>
                Baccarat
            </span>
        </li>

        <li className="nav-item">
            <span className="nav-link" onClick={() => toast.error("Game Locked")}>
                32 Cards
            </span>
        </li>

        <li className="nav-item">
            <span className="nav-link" onClick={() => toast.error("Game Locked")}>
                Teenpatti
            </span>
        </li>

        <li className="nav-item">
            <span className="nav-link" onClick={() => toast.error("Game Locked")}>
                Poker
            </span>
        </li>

        <li className="nav-item">
            <span className="nav-link" onClick={() => toast.error("Game Locked")}>
                Lucky 7
            </span>
        </li>

        <li className="nav-item">
<span
    className="nav-link ce-crash-link"
    onClick={() => toast.error("Game Locked")}
>
    <svg
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 457.6 277.4"
        className="ce-crash-icon"
    >
       <g><path class="cls-1" d="M61.85,273.4c-1.8-3.5-2.3-3.9-4-3-3.2,1.7-5.5,1.1-9-2.5l-3.3-3.4,4.4-1.9c2.4-1.1,8-2.2,12.4-2.6,27.1-2.5,84.1-19.2,161-47.1,32.1-11.7,80.4-30.1,81.3-31,1.2-1.2-1-1.5-11.9-1.7-10.6-.2-11.7,0-16.5,2.4-13.1,6.8-74.6,31.2-92.8,36.7l-6.9,2.1-6.4-5-6.5-4.9-3.6,1.5c-21.3,9-88,36.4-89.2,36.6-1,.2-1.6-.6-1.8-2.3-.3-2.4.5-3,7.8-6.9,4.5-2.3,7.9-4.5,7.5-4.8-.3-.4-4.3-.9-8.9-1.3-9.7-.8-17.3-4-28.2-11.9-4.8-3.5-7.8-5-10-5-4.3,0-6.4.9-6.4,2.7,0,.8,6.8,8.7,15.1,17.6,13,13.7,14.9,16.2,13.3,16.8-3.7,1.5-4.8.8-16.3-10.3-6.4-6.2-14.6-14.2-18.3-17.7l-6.7-6.5-8.8,4.2-8.8,4.2-.3-3.4c-.2-2,.2-4.4.8-5.5s6.5-4.8,13.1-8.2c11.8-6,14.2-8,12.2-10-.7-.7-4.1.5-10.2,3.5l-9.1,4.6v-2.5c0-2,1.4-3.3,7.7-7,10.3-6,17.3-8.1,22.3-6.6,2.1.6,10.7,6.3,19.1,12.6,19,14.3,29.4,19.9,35.9,19.2,5.3-.5,34-13.7,61.9-28.5,18.3-9.7,21.6-12.1,19.5-14.2-.7-.7-7.1,1.8-21.4,8.5-14.7,7-20.5,9.3-21.1,8.4-1.2-2-.1-3.3,5.6-6.3,3-1.6,5.5-3.4,5.5-4,0-.7-1.3-2.5-2.8-4l-2.9-2.7-19.8,9.6c-10.9,5.2-20,9.3-20.2,9.1-.8-.7,2.5-12.8,3.8-14.1.8-.8,9.5-5.6,19.4-10.8,17.7-9.4,18-9.6,17.8-12.7,0-1.7-.4-3.4-.7-3.7s-3.9.9-8,2.7l-7.4,3.3-8.9-9.3c-4.8-5.1-8.9-9.6-9.1-10-.5-1.4,8.8-7.9,14.6-10.1,10.4-4,10.9-3.9,118.5,11.3,35.6,5,65.5,9.7,66.5,10.4,1.6,1.1,1.6,1.4-.1,4.8l-1.7,3.7,2.8,1c1.5.5,5.8,2.1,9.5,3.6l6.9,2.5,10.1-4.3c12.4-5.2,32.9-15.6,45.6-23.2l9.4-5.5,3.2,2.4c3.2,2.4,6.9,3.1,7.9,1.6.3-.5-2.5-6.9-6.3-14.3-3.7-7.4-8.7-18.4-11-24.4-2.4-6.1-4.8-11.6-5.4-12.4-.9-1-3.3-1.2-10.7-.8-10.8.5-18.1,2.6-42,12-15.4,6-67.7,31.5-70.6,34.4-1.3,1.4-3.4,1.4-19.2-.1-9.7-.9-18-1.6-18.4-1.6-1.4,0-.8-5.5,1.1-9.7,1.6-3.4,3.9-5.4,14-12.1,14.3-9.5,28.2-16.5,37.4-18.9l6.5-1.7,9.9,3.9c14.3,5.6,16.3,5.6,39.8-1,38.2-10.6,43.5-11.8,52.2-11.9,8-.1,8.3,0,11.6,3.3,2.6,2.5,5.7,8.3,12,23,4.7,10.8,9,21.3,9.7,23.5,1.7,5.4.8,11.9-2.4,16-6.7,8.8-38,25.2-82.1,42.8-22.8,9.1-61.8,21.9-162.5,53.3-31.1,9.7-64.7,20.3-74.6,23.6-10,3.2-18.9,5.9-19.8,5.9-.8,0-2.5-1.8-3.6-4ZM291.35,168.4c3.8-2.3,7.1-6.9,5.8-8-.6-.5-144.9-20.8-158.8-22.3-1.2-.1-2,.4-2,1.3,0,1.2,15.5,4.6,72,16.1,39.6,8.1,73.7,14.7,75.9,14.8,2.3.1,5.2-.7,7.1-1.9h0ZM247.65,122c4.2-2.3,11.2-5.8,15.4-7.7,4.3-1.9,7.8-3.7,7.8-4.1s-2.8-1.3-6.2-2c-7.7-1.7-13.7-.9-22.4,3.3-6.7,3.1-18.9,11.7-18.1,12.6.6.5,9.3,1.9,13.5,2.2,1.3,0,5.8-1.9,10-4.3h0ZM282.15,115.8c7.5-3.8,10.7-6,10.5-7.1-.4-2.1-18.3-9.2-23.4-9.3-2.2,0-4.9.6-6,1.4q-2.1,1.6,10.4,6.5c2.6,1,4.8,2.3,5,2.9s-1.9,2.2-4.7,3.6c-5.5,2.8-6.6,4-5.7,6.1.8,2.3,2.3,1.9,13.9-4.1h0Z"></path><path class="cls-1" d="M440.55,196.2c-6.8-10.1-13.5-20.3-14.9-22.8-1.5-2.5-5.5-14.1-9.1-25.7l-6.5-21.3,5.1-5c2.7-2.8,5.1-4.9,5.2-4.8.1.2,5.2,9.4,11.4,20.6,11.9,21.3,15.8,31,23.1,58,3.3,11.9,3.3,12.3,1.7,15.7-.9,1.9-2.1,3.5-2.6,3.5s-6.5-8.2-13.4-18.2h0ZM454.65,206.5c.2-1.9-.5-4.6-1.7-6.7-1.8-3.1-22-30.4-24.3-32.9-1.2-1.3-3,1.5-2.2,3.5,1.2,3.4,26,39.9,26.9,39.6.6-.1,1.1-1.7,1.3-3.5h0Z"></path><path class="cls-1" d="M295.35,148.3c-13.2-2.6-24.6-4.9-25.4-5.1-.8-.2,11.3-5.4,27-11.6l28.3-11.4,5.3,5.9c2.9,3.2,5.2,6.3,5.3,6.9,0,.6-2.3,5.5-5.1,10.8-4.8,9.1-5.2,9.6-8.2,9.5-1.8-.1-14-2.3-27.2-5h0Z"></path><path class="cls-1" d="M334.85,152.1c0-.2,1.6-3.6,3.6-7.5,1.9-4,4.1-9,4.9-11.1l1.3-3.8-5.3-6.9c-5.3-6.8-5.4-6.9-3.2-8.1,2-1.1,2.6-.8,6.2,3.2,2.2,2.5,4.3,4.5,4.6,4.5s1.7-3.9,3-8.7l2.4-8.8.3,4.9c.2,2.6-.2,8-.8,11.8l-1,7,5.1,6.4,5.1,6.4-2.7,1c-2.2.9-2.9.6-5-1.8-1.3-1.5-2.5-3-2.5-3.4-.1-2.3-1.9-.1-3.9,4.6-1.2,3-2.8,5.9-3.5,6.5-1.4,1.1-8.6,4.3-8.6,3.8h0Z"></path><path class="cls-1" d="M404.75,114.7c-7.2-16.1-7.3-16.4-5.8-17.5,1.5-1,21.2-.4,24.2.7.9.4,1.7,1.4,1.7,2.2,0,4.2-12.6,21.3-15.6,21.3-.9,0-2.8-2.9-4.5-6.7h0ZM414.15,104.7c4.8-2,8.7-4,8.7-4.4,0-1.2-4.4-1.9-13.2-1.9-8.2,0-8.8.1-8.8,2,0,2.4,2.7,8,3.8,8,.4,0,4.6-1.6,9.5-3.7Z"></path><path class="cls-1" d="M385.05,75.9c-10.7-19.1-14-27.3-20.7-51.6-4-14.3-4.3-18.7-1.6-22l1.9-2.3,14.8,22.3,14.8,22.3,7.6,24.2c4.2,13.2,7.4,24.2,7.1,24.3-.3.1-3.5.3-7.1.6l-6.5.4-10.3-18.2h0ZM391.85,46.5c0-1.5-23.2-37.5-26.1-40.6-1.3-1.3-2.9,1.2-2.9,4.7,0,2.5,8.8,15.4,22.3,32.7,4.6,6,6.7,7,6.7,3.2Z"></path></g>
    </svg>

    <span>Crash</span>
</span>
</li>

    </ul>
</nav>
</div>

   
    <div className="main-content">
    
    <input type="checkbox" id="sidebar-toggle" />

        {/* 2. Dark Overlay. Clicking it closes the menu automatically */}
        <label htmlFor="sidebar-toggle" className="sidebar-overlay"></label>
    
    <aside className="ce-sidebar">

{/* Racing Sports */}
<div className="ce-sidebar-title">
    <span>Racing Sports</span>
    <span>▲</span>
</div>

<div className="ce-sidebar-item" onClick={showLocked}>
    Horse Racing
</div>

<div className="ce-sidebar-item" onClick={showLocked}>
    Greyhound Racing
</div>


{/* Others */}
<div className="ce-sidebar-title">
    <span>Others</span>
    <span>▲</span>
</div>

<div className="ce-sidebar-item ce-blink-item" onClick={showLocked}>
    Our Casino
</div>

<div className="ce-sidebar-item ce-blink-item" onClick={showLocked}>
    Our VIP Casino
</div>

<div className="ce-sidebar-item ce-blink-item" onClick={showLocked}>
    Our Premium Casino
</div>

<div className="ce-sidebar-item ce-blink-item" onClick={showLocked}>
    Our Virtual
</div>
<div className="ce-sidebar-item" onClick={showLocked}>Live Casino</div>
<div className="ce-sidebar-item" onClick={showLocked}>Slot Game</div>
<div className="ce-sidebar-item" onClick={showLocked}>Fantasy Game</div>


{/* All Sports */}
<div className="ce-sidebar-title">
    <span>All Sports</span>
    <span>▲</span>
</div>

<div className="ce-sidebar-item" onClick={showLocked}>
    <span className="ce-plus">⊞</span>
    Politics
</div>

<div className="ce-sidebar-item active">
    <span className="ce-plus">⊞</span>
    Cricket
</div>

<div className="ce-sidebar-item" onClick={showLocked}>
    <span className="ce-plus">⊞</span>
    Football
</div>

<div className="ce-sidebar-item" onClick={showLocked}>
    <span className="ce-plus">⊞</span>
    Tennis
</div>

<div className="ce-sidebar-item" onClick={showLocked}>
    <span className="ce-plus">⊞</span>
    Table Tennis
</div>

<div className="ce-sidebar-item" onClick={showLocked}>
    <span className="ce-plus">⊞</span>
    Badminton
</div>

<div className="ce-sidebar-item" onClick={showLocked}>
    <span className="ce-plus">⊞</span>
    Esoccer
</div>

<div className="ce-sidebar-item" onClick={showLocked}>
    <span className="ce-plus">⊞</span>
    Basketball
</div>

<div className="ce-sidebar-item" onClick={showLocked}>
    <span className="ce-plus">⊞</span>
    Volleyball
</div>

<div className="ce-sidebar-item" onClick={showLocked}>
    <span className="ce-plus">⊞</span>
    Snooker
</div>

<div className="ce-sidebar-item" onClick={showLocked}>
    <span className="ce-plus">⊞</span>
   Ice Hockey
</div>

<div className="ce-sidebar-item" onClick={showLocked}>
    <span className="ce-plus">⊞</span>
    E Games
</div>

<div className="ce-sidebar-item" onClick={showLocked}>
    <span className="ce-plus">⊞</span>
    Futsal
</div>

<div className="ce-sidebar-item" onClick={showLocked}>
    <span className="ce-plus">⊞</span>
    Handball
</div>

<div className="ce-sidebar-item" onClick={showLocked}>
    <span className="ce-plus">⊞</span>
    Kabaddi
</div>

<div className="ce-sidebar-item" onClick={showLocked}>
    <span className="ce-plus">⊞</span>
    Golf
</div>

<div className="ce-sidebar-item" onClick={showLocked}>
    <span className="ce-plus">⊞</span>
    Rugby League
</div>

<div className="ce-sidebar-item" onClick={showLocked}>
    <span className="ce-plus">⊞</span>
    Boxing
</div>

<div className="ce-sidebar-item" onClick={showLocked}>
    <span className="ce-plus">⊞</span>
    Beach Volleyball
</div>

<div className="ce-sidebar-item" onClick={showLocked}>
    <span className="ce-plus">⊞</span>
    Mixed Martial Arts
</div>

<div className="ce-sidebar-item" onClick={showLocked}>
    <span className="ce-plus">⊞</span>
    MotoGP
</div>

<div className="ce-sidebar-item" onClick={showLocked}>
    <span className="ce-plus">⊞</span>
    Chess
</div>

<div className="ce-sidebar-item" onClick={showLocked}>
    <span className="ce-plus">⊞</span>
    Cycling
</div>

<div className="ce-sidebar-item" onClick={showLocked}>
    <span className="ce-plus">⊞</span>
    Motorbikes
</div>

<div className="ce-sidebar-item" onClick={showLocked}>
    <span className="ce-plus">⊞</span>
    Athletics
</div>

<div className="ce-sidebar-item" onClick={showLocked}>
    <span className="ce-plus">⊞</span>
    Basketball 3X3
</div>

<div className="ce-sidebar-item" onClick={showLocked}>
    <span className="ce-plus">⊞</span>
    Sumo
</div>

<div className="ce-sidebar-item" onClick={showLocked}>
    <span className="ce-plus">⊞</span>
    Virtual Sports
</div>

<div className="ce-sidebar-item" onClick={showLocked}>
    <span className="ce-plus">⊞</span>
    Motor Sports
</div>

<div className="ce-sidebar-item" onClick={showLocked}>
    <span className="ce-plus">⊞</span>
    Baseball
</div>

<div className="ce-sidebar-item" onClick={showLocked}>
    <span className="ce-plus">⊞</span>
    Rugby Union
</div>

<div className="ce-sidebar-item" onClick={showLocked}>
    <span className="ce-plus">⊞</span>
    Darts
</div>

<div className="ce-sidebar-item" onClick={showLocked}>
    <span className="ce-plus">⊞</span>
    American Football
</div>

<div className="ce-sidebar-item" onClick={showLocked}>
    <span className="ce-plus">⊞</span>
    Soccer
</div>

<div className="ce-sidebar-item" onClick={showLocked}>
    <span className="ce-plus">⊞</span>
    Esports
</div>

<div className="ce-sidebar-item" onClick={showLocked}>
    <span className="ce-plus">⊞</span>
    Boat Racing
</div>

<div className="ce-sidebar-item" onClick={showLocked}>
    <span className="ce-plus">⊞</span>
    Wrestling
</div>

    </aside>

    <div className="ce-content">

    <ul className="sports-tab">

        <li className="active">
            <span>Cricket</span>
        </li>

        <li onClick={showLocked}>
            <span>Football</span>
        </li>

        <li onClick={showLocked}>
            <span>Tennis</span>
        </li>

        <li onClick={showLocked}>
            <span>Table Tennis</span>
        </li>

        <li onClick={showLocked}>
            <span>Horse Racing</span>
        </li>

        <li onClick={showLocked}>
            <span>Greyhound Racing</span>
        </li>

        <li onClick={showLocked}>
            <span>Basketball</span>
        </li>

        <li onClick={showLocked}>
            <span>Esoccer</span>
        </li>

        <li onClick={showLocked}>
            <span>Wrestling</span>
        </li>

        <li onClick={showLocked}>
            <span>Volleyball</span>
        </li>

        <li onClick={showLocked}>
            <span>Badminton</span>
        </li>

        <li onClick={showLocked}>
            <span>Snooker</span>
        </li>

        <li onClick={showLocked}>
            <span>Darts</span>
        </li>

        <li onClick={showLocked}>
            <span>Boxing</span>
        </li>

        <li onClick={showLocked}>
            <span>Mixed Martial Arts</span>
        </li>

        <li onClick={showLocked}>
            <span>American Football</span>
        </li>

        <li onClick={showLocked}>
            <span>E Games</span>
        </li>

        <li onClick={showLocked}>
            <span>Ice Hockey</span>
        </li>

        <li onClick={showLocked}>
            <span>Futsal</span>
        </li>

        <li onClick={showLocked}>
            <span>Motor Sports</span>
        </li>

        <li onClick={showLocked}>
            <span>Politics</span>
        </li>

        <li onClick={showLocked}>
            <span>Kabaddi</span>
        </li>

    </ul>





    {/* Mobile scrollable container wrapper */}
    <div className="mobile-scroll-container">
        <div className="cricket-section">
            {loading && matches.length === 0 ? (
                <div className="cricket-loadingx"></div>
            ) : (
                <div className="matches-grid">

                <div className="match-grid-header">
                    <div className="header-game-col">Game</div>
                    <div className="header-odds-col">
                        <span>1</span>
                        <span>X</span>
                        <span>2</span>
                    </div>
                </div>
                    
                    {filteredMatches.length > 0 ? (
                        filteredMatches.map(match => (
                            <MatchCard 
                                key={match.marketid} 
                                match={match}
                                formatDateTime={formatDateTime}
                            />
                        ))
                    ) : (
                        <div className="no-matches"></div>
                    )}
                </div>
            )}
        </div>
    </div>

                
    <div className="casino-list mt-2">
    <div className="casino-list-item">
        <div onClick={() => toast.error("Game Locked")} style={{ cursor: 'pointer' }}>
            <div className="casino-list-item-banner" style={{ backgroundImage: 'url("https://dataobj.ecoassetsservice.com/casino-icons/lc/goal2.jpg"), url("https://dataobj.ecoassetsservice.com/casino-icons/default.jpg")' }}></div>
            <div className="casino-list-name">Goal 2</div>
        </div>
    </div>
    <div className="casino-list-item">
        <div onClick={() => toast.error("Game Locked")} style={{ cursor: 'pointer' }}>
            <div className="casino-list-item-banner" style={{ backgroundImage: 'url("https://dataobj.ecoassetsservice.com/casino-icons/lc/worli3.gif"), url("https://dataobj.ecoassetsservice.com/casino-icons/default.jpg")' }}></div>
            <div className="casino-list-name">Matka</div>
        </div>
    </div>
    <div className="casino-list-item">
        <div onClick={() => toast.error("Game Locked")} style={{ cursor: 'pointer' }}>
            <div className="casino-list-item-banner" style={{ backgroundImage: 'url("https://dataobj.ecoassetsservice.com/casino-icons/lc/teen62.gif"), url("https://dataobj.ecoassetsservice.com/casino-icons/default.jpg")' }}></div>
            <div className="casino-list-name">V VIP Teenpatti 1-day</div>
        </div>
    </div>
    <div className="casino-list-item">
        <div onClick={() => toast.error("Game Locked")} style={{ cursor: 'pointer' }}>
            <div className="casino-list-item-banner" style={{ backgroundImage: 'url("https://dataobj.ecoassetsservice.com/casino-icons/lc/dolidana.gif"), url("https://dataobj.ecoassetsservice.com/casino-icons/default.jpg")' }}></div>
            <div className="casino-list-name">Dolidana</div>
        </div>
    </div>
    <div className="casino-list-item">
        <div onClick={() => toast.error("Game Locked")} style={{ cursor: 'pointer' }}>
            <div className="casino-list-item-banner" style={{ backgroundImage: 'url("https://dataobj.ecoassetsservice.com/casino-icons/lc/mogambo.gif"), url("https://dataobj.ecoassetsservice.com/casino-icons/default.jpg")' }}></div>
            <div className="casino-list-name">Mogambo</div>
        </div>
    </div>
    <div className="casino-list-item">
        <div onClick={() => toast.error("Game Locked")} style={{ cursor: 'pointer' }}>
            <div className="casino-list-item-banner" style={{ backgroundImage: 'url("https://dataobj.ecoassetsservice.com/casino-icons/lc/teen20v1.jpg"), url("https://dataobj.ecoassetsservice.com/casino-icons/default.jpg")' }}></div>
            <div className="casino-list-name">20-20 Teenpatti VIP1</div>
        </div>
    </div>
    <div className="casino-list-item">
        <div onClick={() => toast.error("Game Locked")} style={{ cursor: 'pointer' }}>
            <div className="casino-list-item-banner" style={{ backgroundImage: 'url("https://dataobj.ecoassetsservice.com/casino-icons/lc/lucky5.jpg"), url("https://dataobj.ecoassetsservice.com/casino-icons/default.jpg")' }}></div>
            <div className="casino-list-name">Lucky 6</div>
        </div>
    </div>
    <div className="casino-list-item">
        <div onClick={() => toast.error("Game Locked")} style={{ cursor: 'pointer' }}>
            <div className="casino-list-item-banner" style={{ backgroundImage: 'url("https://dataobj.ecoassetsservice.com/casino-icons/lc/roulette12.jpg"), url("https://dataobj.ecoassetsservice.com/casino-icons/default.jpg")' }}></div>
            <div className="casino-list-name">Beach Roulette</div>
        </div>
    </div>
    <div className="casino-list-item">
        <div onClick={() => toast.error("Game Locked")} style={{ cursor: 'pointer' }}>
            <div className="casino-list-item-banner" style={{ backgroundImage: 'url("https://dataobj.ecoassetsservice.com/casino-icons/lc/roulette13.jpg"), url("https://dataobj.ecoassetsservice.com/casino-icons/default.jpg")' }}></div>
            <div className="casino-list-name">Roulette</div>
        </div>
    </div>
    <div className="casino-list-item">
        <div onClick={() => toast.error("Game Locked")} style={{ cursor: 'pointer' }}>
            <div className="casino-list-item-banner" style={{ backgroundImage: 'url("https://dataobj.ecoassetsservice.com/casino-icons/lc/roulette11.jpg"), url("https://dataobj.ecoassetsservice.com/casino-icons/default.jpg")' }}></div>
            <div className="casino-list-name">Golden Roulette</div>
        </div>
    </div>
    <div className="casino-list-item">
        <div onClick={() => toast.error("Game Locked")} style={{ cursor: 'pointer' }}>
            <div className="casino-list-item-banner" style={{ backgroundImage: 'url("https://dataobj.ecoassetsservice.com/casino-icons/lc/poison.jpg"), url("https://dataobj.ecoassetsservice.com/casino-icons/default.jpg")' }}></div>
            <div className="casino-list-name">Teenpatti Poison One Day</div>
        </div>
    </div>
    <div className="casino-list-item">
        <div onClick={() => toast.error("Game Locked")} style={{ cursor: 'pointer' }}>
            <div className="casino-list-item-banner" style={{ backgroundImage: 'url("https://dataobj.ecoassetsservice.com/casino-icons/lc/teenunique.jpg"), url("https://dataobj.ecoassetsservice.com/casino-icons/default.jpg")' }}></div>
            <div className="casino-list-name">Unique Teenpatti</div>
        </div>
    </div>
    <div className="casino-list-item">
        <div onClick={() => toast.error("Game Locked")} style={{ cursor: 'pointer' }}>
            <div className="casino-list-item-banner" style={{ backgroundImage: 'url("https://dataobj.ecoassetsservice.com/casino-icons/lc/poison20.jpg"), url("https://dataobj.ecoassetsservice.com/casino-icons/default.jpg")' }}></div>
            <div className="casino-list-name">Teenpatti Poison 20-20</div>
        </div>
    </div>
    <div className="casino-list-item">
        <div onClick={() => toast.error("Game Locked")} style={{ cursor: 'pointer' }}>
            <div className="casino-list-item-banner" style={{ backgroundImage: 'url("https://dataobj.ecoassetsservice.com/casino-icons/lc/joker120.jpg"), url("https://dataobj.ecoassetsservice.com/casino-icons/default.jpg")' }}></div>
            <div className="casino-list-name">Unlimited Joker 20-20</div>
        </div>
    </div>
    <div className="casino-list-item">
        <div onClick={() => toast.error("Game Locked")} style={{ cursor: 'pointer' }}>
            <div className="casino-list-item-banner" style={{ backgroundImage: 'url("https://dataobj.ecoassetsservice.com/casino-icons/lc/joker20.jpg"), url("https://dataobj.ecoassetsservice.com/casino-icons/default.jpg")' }}></div>
            <div className="casino-list-name">Teenpatti Joker 20-20</div>
        </div>
    </div>
    <div className="casino-list-item">
        <div onClick={() => toast.error("Game Locked")} style={{ cursor: 'pointer' }}>
            <div className="casino-list-item-banner" style={{ backgroundImage: 'url("https://dataobj.ecoassetsservice.com/casino-icons/lc/joker1.jpg"), url("https://dataobj.ecoassetsservice.com/casino-icons/default.jpg")' }}></div>
            <div className="casino-list-name">Unlimited Joker Oneday</div>
        </div>
    </div>
    <div className="casino-list-item">
        <div onClick={() => toast.error("Game Locked")} style={{ cursor: 'pointer' }}>
            <div className="casino-list-item-banner" style={{ backgroundImage: 'url("https://dataobj.ecoassetsservice.com/casino-icons/lc/teen20c.jpg"), url("https://dataobj.ecoassetsservice.com/casino-icons/default.jpg")' }}></div>
            <div className="casino-list-name">20-20 Teenpatti C</div>
        </div>
    </div>
    <div className="casino-list-item">
        <div onClick={() => toast.error("Game Locked")} style={{ cursor: 'pointer' }}>
            <div className="casino-list-item-banner" style={{ backgroundImage: 'url("https://dataobj.ecoassetsservice.com/casino-icons/lc/btable2.jpg"), url("https://dataobj.ecoassetsservice.com/casino-icons/default.jpg")' }}></div>
            <div className="casino-list-name">Bollywood Casino 2</div>
        </div>
    </div>
    <div className="casino-list-item">
        <div onClick={() => toast.error("Game Locked")} style={{ cursor: 'pointer' }}>
            <div className="casino-list-item-banner" style={{ backgroundImage: 'url("https://dataobj.ecoassetsservice.com/casino-icons/lc/ourroullete.jpg"), url("https://dataobj.ecoassetsservice.com/casino-icons/default.jpg")' }}></div>
            <div className="casino-list-name">Unique Roulette</div>
        </div>
    </div>
    <div className="casino-list-item">
        <div onClick={() => toast.error("Game Locked")} style={{ cursor: 'pointer' }}>
            <div className="casino-list-item-banner" style={{ backgroundImage: 'url("https://dataobj.ecoassetsservice.com/casino-icons/lc/superover3.jpg"), url("https://dataobj.ecoassetsservice.com/casino-icons/default.jpg")' }}></div>
            <div className="casino-list-name">Mini Superover</div>
        </div>
    </div>
    <div className="casino-list-item">
        <div onClick={() => toast.error("Game Locked")} style={{ cursor: 'pointer' }}>
            <div className="casino-list-item-banner" style={{ backgroundImage: 'url("https://dataobj.ecoassetsservice.com/casino-icons/lc/goal.jpg"), url("https://dataobj.ecoassetsservice.com/casino-icons/default.jpg")' }}></div>
            <div className="casino-list-name">Goal</div>
        </div>
    </div>
    <div className="casino-list-item">
        <div onClick={() => toast.error("Game Locked")} style={{ cursor: 'pointer' }}>
            <div className="casino-list-item-banner" style={{ backgroundImage: 'url("https://dataobj.ecoassetsservice.com/casino-icons/lc/ab4.jpg"), url("https://dataobj.ecoassetsservice.com/casino-icons/default.jpg")' }}></div>
            <div className="casino-list-name">ANDAR BAHAR 150 cards</div>
        </div>
    </div>
    <div className="casino-list-item">
        <div onClick={() => toast.error("Game Locked")} style={{ cursor: 'pointer' }}>
            <div className="casino-list-item-banner" style={{ backgroundImage: 'url("https://dataobj.ecoassetsservice.com/casino-icons/lc/lucky15.jpg"), url("https://dataobj.ecoassetsservice.com/casino-icons/default.jpg")' }}></div>
            <div className="casino-list-name">Lucky 15</div>
        </div>
    </div>
    <div className="casino-list-item">
        <div onClick={() => toast.error("Game Locked")} style={{ cursor: 'pointer' }}>
            <div className="casino-list-item-banner" style={{ backgroundImage: 'url("https://dataobj.ecoassetsservice.com/casino-icons/lc/superover2.jpg"), url("https://dataobj.ecoassetsservice.com/casino-icons/default.jpg")' }}></div>
            <div className="casino-list-name">Super Over2</div>
        </div>
    </div>
    <div className="casino-list-item">
        <div onClick={() => toast.error("Game Locked")} style={{ cursor: 'pointer' }}>
            <div className="casino-list-item-banner" style={{ backgroundImage: 'url("https://dataobj.ecoassetsservice.com/casino-icons/lc/teen41.jpg"), url("https://dataobj.ecoassetsservice.com/casino-icons/default.jpg")' }}></div>
            <div className="casino-list-name">Queen Top Open Teenpatti</div>
        </div>
    </div>
    <div className="casino-list-item">
        <div onClick={() => toast.error("Game Locked")} style={{ cursor: 'pointer' }}>
            <div className="casino-list-item-banner" style={{ backgroundImage: 'url("https://dataobj.ecoassetsservice.com/casino-icons/lc/teen42.jpg"), url("https://dataobj.ecoassetsservice.com/casino-icons/default.jpg")' }}></div>
            <div className="casino-list-name">Jack Top Open Teenpatti</div>
        </div>
    </div>
    <div className="casino-list-item">
        <div onClick={() => toast.error("Game Locked")} style={{ cursor: 'pointer' }}>
            <div className="casino-list-item-banner" style={{ backgroundImage: 'url("https://dataobj.ecoassetsservice.com/casino-icons/lc/sicbo2.jpg"), url("https://dataobj.ecoassetsservice.com/casino-icons/default.jpg")' }}></div>
            <div className="casino-list-name">Sic Bo2</div>
        </div>
    </div>
    <div className="casino-list-item">
        <div onClick={() => toast.error("Game Locked")} style={{ cursor: 'pointer' }}>
            <div className="casino-list-item-banner" style={{ backgroundImage: 'url("https://dataobj.ecoassetsservice.com/casino-icons/lc/teen33.jpg"), url("https://dataobj.ecoassetsservice.com/casino-icons/default.jpg")' }}></div>
            <div className="casino-list-name">Instant Teenpatti 3.0</div>
        </div>
    </div>
    <div className="casino-list-item">
        <div onClick={() => toast.error("Game Locked")} style={{ cursor: 'pointer' }}>
            <div className="casino-list-item-banner" style={{ backgroundImage: 'url("https://dataobj.ecoassetsservice.com/casino-icons/lc/sicbo.jpg"), url("https://dataobj.ecoassetsservice.com/casino-icons/default.jpg")' }}></div>
            <div className="casino-list-name">Sic Bo</div>
        </div>
    </div>
    <div className="casino-list-item">
        <div onClick={() => toast.error("Game Locked")} style={{ cursor: 'pointer' }}>
            <div className="casino-list-item-banner" style={{ backgroundImage: 'url("https://dataobj.ecoassetsservice.com/casino-icons/lc/ballbyball.jpg"), url("https://dataobj.ecoassetsservice.com/casino-icons/default.jpg")' }}></div>
            <div className="casino-list-name">Ball by Ball</div>
        </div>
    </div>
    <div className="casino-list-item">
        <div onClick={() => toast.error("Game Locked")} style={{ cursor: 'pointer' }}>
            <div className="casino-list-item-banner" style={{ backgroundImage: 'url("https://dataobj.ecoassetsservice.com/casino-icons/lc/teen32.jpg"), url("https://dataobj.ecoassetsservice.com/casino-icons/default.jpg")' }}></div>
            <div className="casino-list-name">Instant Teenpatti 2.0</div>
        </div>
    </div>
    <div className="casino-list-item">
        <div onClick={() => toast.error("Game Locked")} style={{ cursor: 'pointer' }}>
            <div className="casino-list-item-banner" style={{ backgroundImage: 'url("https://dataobj.ecoassetsservice.com/casino-icons/lc/teen.jpg"), url("https://dataobj.ecoassetsservice.com/casino-icons/default.jpg")' }}></div>
            <div className="casino-list-name">Teenpatti 1-day</div>
        </div>
    </div>
    <div className="casino-list-item">
        <div onClick={() => toast.error("Game Locked")} style={{ cursor: 'pointer' }}>
            <div className="casino-list-item-banner" style={{ backgroundImage: 'url("https://dataobj.ecoassetsservice.com/casino-icons/lc/teen20.jpg"), url("https://dataobj.ecoassetsservice.com/casino-icons/default.jpg")' }}></div>
            <div className="casino-list-name">20-20 Teenpatti</div>
        </div>
    </div>
    <div className="casino-list-item">
        <div onClick={() => toast.error("Game Locked")} style={{ cursor: 'pointer' }}>
            <div className="casino-list-item-banner" style={{ backgroundImage: 'url("https://dataobj.ecoassetsservice.com/casino-icons/lc/teen9.jpg"), url("https://dataobj.ecoassetsservice.com/casino-icons/default.jpg")' }}></div>
            <div className="casino-list-name">Teenpatti Test</div>
        </div>
    </div>
    <div className="casino-list-item">
        <div onClick={() => toast.error("Game Locked")} style={{ cursor: 'pointer' }}>
            <div className="casino-list-item-banner" style={{ backgroundImage: 'url("https://dataobj.ecoassetsservice.com/casino-icons/lc/teen8.jpg"), url("https://dataobj.ecoassetsservice.com/casino-icons/default.jpg")' }}></div>
            <div className="casino-list-name">Teenpatti Open</div>
        </div>
    </div>
    <div className="casino-list-item">
        <div onClick={() => toast.error("Game Locked")} style={{ cursor: 'pointer' }}>
            <div className="casino-list-item-banner" style={{ backgroundImage: 'url("https://dataobj.ecoassetsservice.com/casino-icons/lc/poker.jpg"), url("https://dataobj.ecoassetsservice.com/casino-icons/default.jpg")' }}></div>
            <div className="casino-list-name">Poker 1-Day</div>
        </div>
    </div>
    <div className="casino-list-item">
        <div onClick={() => toast.error("Game Locked")} style={{ cursor: 'pointer' }}>
            <div className="casino-list-item-banner" style={{ backgroundImage: 'url("https://dataobj.ecoassetsservice.com/casino-icons/lc/poker20.jpg"), url("https://dataobj.ecoassetsservice.com/casino-icons/default.jpg")' }}></div>
            <div className="casino-list-name">20-20 Poker</div>
        </div>
    </div>
    <div className="casino-list-item">
        <div onClick={() => toast.error("Game Locked")} style={{ cursor: 'pointer' }}>
            <div className="casino-list-item-banner" style={{ backgroundImage: 'url("https://dataobj.ecoassetsservice.com/casino-icons/lc/poker6.jpg"), url("https://dataobj.ecoassetsservice.com/casino-icons/default.jpg")' }}></div>
            <div className="casino-list-name">Poker 6 Players</div>
        </div>
    </div>
    <div className="casino-list-item">
        <div onClick={() => toast.error("Game Locked")} style={{ cursor: 'pointer' }}>
            <div className="casino-list-item-banner" style={{ backgroundImage: 'url("https://dataobj.ecoassetsservice.com/casino-icons/lc/baccarat.jpg"), url("https://dataobj.ecoassetsservice.com/casino-icons/default.jpg")' }}></div>
            <div className="casino-list-name">Baccarat</div>
        </div>
    </div>
    <div className="casino-list-item">
        <div onClick={() => toast.error("Game Locked")} style={{ cursor: 'pointer' }}>
            <div className="casino-list-item-banner" style={{ backgroundImage: 'url("https://dataobj.ecoassetsservice.com/casino-icons/lc/baccarat2.jpg"), url("https://dataobj.ecoassetsservice.com/casino-icons/default.jpg")' }}></div>
            <div className="casino-list-name">Baccarat 2</div>
        </div>
    </div>
    <div className="casino-list-item">
        <div onClick={() => toast.error("Game Locked")} style={{ cursor: 'pointer' }}>
            <div className="casino-list-item-banner" style={{ backgroundImage: 'url("https://dataobj.ecoassetsservice.com/casino-icons/lc/dt20.jpg"), url("https://dataobj.ecoassetsservice.com/casino-icons/default.jpg")' }}></div>
            <div className="casino-list-name">20-20 Dragon Tiger</div>
        </div>
    </div>
    <div className="casino-list-item">
        <div onClick={() => toast.error("Game Locked")} style={{ cursor: 'pointer' }}>
            <div className="casino-list-item-banner" style={{ backgroundImage: 'url("https://dataobj.ecoassetsservice.com/casino-icons/lc/dt6.jpg"), url("https://dataobj.ecoassetsservice.com/casino-icons/default.jpg")' }}></div>
            <div className="casino-list-name">1 Day Dragon Tiger</div>
        </div>
    </div>
    <div className="casino-list-item">
        <div onClick={() => toast.error("Game Locked")} style={{ cursor: 'pointer' }}>
            <div className="casino-list-item-banner" style={{ backgroundImage: 'url("https://dataobj.ecoassetsservice.com/casino-icons/lc/dtl20.jpg"), url("https://dataobj.ecoassetsservice.com/casino-icons/default.jpg")' }}></div>
            <div className="casino-list-name">20-20 D T L</div>
        </div>
    </div>
    <div className="casino-list-item">
        <div onClick={() => toast.error("Game Locked")} style={{ cursor: 'pointer' }}>
            <div className="casino-list-item-banner" style={{ backgroundImage: 'url("https://dataobj.ecoassetsservice.com/casino-icons/lc/dt202.jpg"), url("https://dataobj.ecoassetsservice.com/casino-icons/default.jpg")' }}></div>
            <div className="casino-list-name">20-20 Dragon Tiger 2</div>
        </div>
    </div>
    <div className="casino-list-item">
        <div onClick={() => toast.error("Game Locked")} style={{ cursor: 'pointer' }}>
            <div className="casino-list-item-banner" style={{ backgroundImage: 'url("https://dataobj.ecoassetsservice.com/casino-icons/lc/card32.jpg"), url("https://dataobj.ecoassetsservice.com/casino-icons/default.jpg")' }}></div>
            <div className="casino-list-name">32 Cards A</div>
        </div>
    </div>
    <div className="casino-list-item">
        <div onClick={() => toast.error("Game Locked")} style={{ cursor: 'pointer' }}>
            <div className="casino-list-item-banner" style={{ backgroundImage: 'url("https://dataobj.ecoassetsservice.com/casino-icons/lc/card32eu.jpg"), url("https://dataobj.ecoassetsservice.com/casino-icons/default.jpg")' }}></div>
            <div className="casino-list-name">32 Cards B</div>
        </div>
    </div>
    <div className="casino-list-item">
        <div onClick={() => toast.error("Game Locked")} style={{ cursor: 'pointer' }}>
            <div className="casino-list-item-banner" style={{ backgroundImage: 'url("https://dataobj.ecoassetsservice.com/casino-icons/lc/ab20.jpg"), url("https://dataobj.ecoassetsservice.com/casino-icons/default.jpg")' }}></div>
            <div className="casino-list-name">Andar Bahar</div>
        </div>
    </div>
    <div className="casino-list-item">
        <div onClick={() => toast.error("Game Locked")} style={{ cursor: 'pointer' }}>
            <div className="casino-list-item-banner" style={{ backgroundImage: 'url("https://dataobj.ecoassetsservice.com/casino-icons/lc/abj.jpg"), url("https://dataobj.ecoassetsservice.com/casino-icons/default.jpg")' }}></div>
            <div className="casino-list-name">Andar Bahar 2</div>
        </div>
    </div>
    <div className="casino-list-item">
        <div onClick={() => toast.error("Game Locked")} style={{ cursor: 'pointer' }}>
            <div className="casino-list-item-banner" style={{ backgroundImage: 'url("https://dataobj.ecoassetsservice.com/casino-icons/lc/lucky7.jpg"), url("https://dataobj.ecoassetsservice.com/casino-icons/default.jpg")' }}></div>
            <div className="casino-list-name">Lucky 7 - A</div>
        </div>
    </div>
    <div className="casino-list-item">
        <div onClick={() => toast.error("Game Locked")} style={{ cursor: 'pointer' }}>
            <div className="casino-list-item-banner" style={{ backgroundImage: 'url("https://dataobj.ecoassetsservice.com/casino-icons/lc/lucky7eu.jpg"), url("https://dataobj.ecoassetsservice.com/casino-icons/default.jpg")' }}></div>
            <div className="casino-list-name">Lucky 7 - B</div>
        </div>
    </div>
    <div className="casino-list-item">
        <div onClick={() => toast.error("Game Locked")} style={{ cursor: 'pointer' }}>
            <div className="casino-list-item-banner" style={{ backgroundImage: 'url("https://dataobj.ecoassetsservice.com/casino-icons/lc/3cardj.jpg"), url("https://dataobj.ecoassetsservice.com/casino-icons/default.jpg")' }}></div>
            <div className="casino-list-name">3 Cards Judgement</div>
        </div>
    </div>
    <div className="casino-list-item">
        <div onClick={() => toast.error("Game Locked")} style={{ cursor: 'pointer' }}>
            <div className="casino-list-item-banner" style={{ backgroundImage: 'url("https://dataobj.ecoassetsservice.com/casino-icons/lc/war.jpg"), url("https://dataobj.ecoassetsservice.com/casino-icons/default.jpg")' }}></div>
            <div className="casino-list-name">Casino War</div>
        </div>
    </div>
    <div className="casino-list-item">
        <div onClick={() => toast.error("Game Locked")} style={{ cursor: 'pointer' }}>
            <div className="casino-list-item-banner" style={{ backgroundImage: 'url("https://dataobj.ecoassetsservice.com/casino-icons/lc/worli.jpg"), url("https://dataobj.ecoassetsservice.com/casino-icons/default.jpg")' }}></div>
            <div className="casino-list-name">Worli Matka</div>
        </div>
    </div>
    <div className="casino-list-item">
        <div onClick={() => toast.error("Game Locked")} style={{ cursor: 'pointer' }}>
            <div className="casino-list-item-banner" style={{ backgroundImage: 'url("https://dataobj.ecoassetsservice.com/casino-icons/lc/worli2.jpg"), url("https://dataobj.ecoassetsservice.com/casino-icons/default.jpg")' }}></div>
            <div className="casino-list-name">Instant Worli</div>
        </div>
    </div>
    <div className="casino-list-item">
        <div onClick={() => toast.error("Game Locked")} style={{ cursor: 'pointer' }}>
            <div className="casino-list-item-banner" style={{ backgroundImage: 'url("https://dataobj.ecoassetsservice.com/casino-icons/lc/aaa.jpg"), url("https://dataobj.ecoassetsservice.com/casino-icons/default.jpg")' }}></div>
            <div className="casino-list-name">Amar Akbar Anthony</div>
        </div>
    </div>
    <div className="casino-list-item">
        <div onClick={() => toast.error("Game Locked")} style={{ cursor: 'pointer' }}>
            <div className="casino-list-item-banner" style={{ backgroundImage: 'url("https://dataobj.ecoassetsservice.com/casino-icons/lc/btable.jpg"), url("https://dataobj.ecoassetsservice.com/casino-icons/default.jpg")' }}></div>
            <div className="casino-list-name">Bollywood Casino</div>
        </div>
    </div>
    <div className="casino-list-item">
        <div onClick={() => toast.error("Game Locked")} style={{ cursor: 'pointer' }}>
            <div className="casino-list-item-banner" style={{ backgroundImage: 'url("https://dataobj.ecoassetsservice.com/casino-icons/lc/lottcard.jpg"), url("https://dataobj.ecoassetsservice.com/casino-icons/default.jpg")' }}></div>
            <div className="casino-list-name">Lottery</div>
        </div>
    </div>
    <div className="casino-list-item">
        <div onClick={() => toast.error("Game Locked")} style={{ cursor: 'pointer' }}>
            <div className="casino-list-item-banner" style={{ backgroundImage: 'url("https://dataobj.ecoassetsservice.com/casino-icons/lc/cricketv3.jpg"), url("https://dataobj.ecoassetsservice.com/casino-icons/default.jpg")' }}></div>
            <div className="casino-list-name">5Five Cricket</div>
        </div>
    </div>
    <div className="casino-list-item">
        <div onClick={() => toast.error("Game Locked")} style={{ cursor: 'pointer' }}>
            <div className="casino-list-item-banner" style={{ backgroundImage: 'url("https://dataobj.ecoassetsservice.com/casino-icons/lc/cmatch20.jpg"), url("https://dataobj.ecoassetsservice.com/casino-icons/default.jpg")' }}></div>
            <div className="casino-list-name">Cricket Match 20-20</div>
        </div>
    </div>
    <div className="casino-list-item">
        <div onClick={() => toast.error("Game Locked")} style={{ cursor: 'pointer' }}>
            <div className="casino-list-item-banner" style={{ backgroundImage: 'url("https://dataobj.ecoassetsservice.com/casino-icons/lc/cmeter.jpg"), url("https://dataobj.ecoassetsservice.com/casino-icons/default.jpg")' }}></div>
            <div className="casino-list-name">Casino Meter</div>
        </div>
    </div>
    <div className="casino-list-item">
        <div onClick={() => toast.error("Game Locked")} style={{ cursor: 'pointer' }}>
            <div className="casino-list-item-banner" style={{ backgroundImage: 'url("https://dataobj.ecoassetsservice.com/casino-icons/lc/teen6.jpg"), url("https://dataobj.ecoassetsservice.com/casino-icons/default.jpg")' }}></div>
            <div className="casino-list-name">Teenpatti - 2.0</div>
        </div>
    </div>
    <div className="casino-list-item">
        <div onClick={() => toast.error("Game Locked")} style={{ cursor: 'pointer' }}>
            <div className="casino-list-item-banner" style={{ backgroundImage: 'url("https://dataobj.ecoassetsservice.com/casino-icons/lc/queen.jpg"), url("https://dataobj.ecoassetsservice.com/casino-icons/default.jpg")' }}></div>
            <div className="casino-list-name">Queen</div>
        </div>
    </div>
    <div className="casino-list-item">
        <div onClick={() => toast.error("Game Locked")} style={{ cursor: 'pointer' }}>
            <div className="casino-list-item-banner" style={{ backgroundImage: 'url("https://dataobj.ecoassetsservice.com/casino-icons/lc/race20.jpg"), url("https://dataobj.ecoassetsservice.com/casino-icons/default.jpg")' }}></div>
            <div className="casino-list-name">Race20</div>
        </div>
    </div>
    <div className="casino-list-item">
        <div onClick={() => toast.error("Game Locked")} style={{ cursor: 'pointer' }}>
            <div className="casino-list-item-banner" style={{ backgroundImage: 'url("https://dataobj.ecoassetsservice.com/casino-icons/lc/lucky7eu2.jpg"), url("https://dataobj.ecoassetsservice.com/casino-icons/default.jpg")' }}></div>
            <div className="casino-list-name">Lucky 7 - C</div>
        </div>
    </div>
    <div className="casino-list-item">
        <div onClick={() => toast.error("Game Locked")} style={{ cursor: 'pointer' }}>
            <div className="casino-list-item-banner" style={{ backgroundImage: 'url("https://dataobj.ecoassetsservice.com/casino-icons/lc/superover.jpg"), url("https://dataobj.ecoassetsservice.com/casino-icons/default.jpg")' }}></div>
            <div className="casino-list-name">Super Over</div>
        </div>
    </div>
    <div className="casino-list-item">
        <div onClick={() => toast.error("Game Locked")} style={{ cursor: 'pointer' }}>
            <div className="casino-list-item-banner" style={{ backgroundImage: 'url("https://dataobj.ecoassetsservice.com/casino-icons/lc/trap.jpg"), url("https://dataobj.ecoassetsservice.com/casino-icons/default.jpg")' }}></div>
            <div className="casino-list-name">The Trap</div>
        </div>
    </div>
    <div className="casino-list-item">
        <div onClick={() => toast.error("Game Locked")} style={{ cursor: 'pointer' }}>
            <div className="casino-list-item-banner" style={{ backgroundImage: 'url("https://dataobj.ecoassetsservice.com/casino-icons/lc/patti2.jpg"), url("https://dataobj.ecoassetsservice.com/casino-icons/default.jpg")' }}></div>
            <div className="casino-list-name">2 Cards Teenpatti</div>
        </div>
    </div>
    <div className="casino-list-item">
        <div onClick={() => toast.error("Game Locked")} style={{ cursor: 'pointer' }}>
            <div className="casino-list-item-banner" style={{ backgroundImage: 'url("https://dataobj.ecoassetsservice.com/casino-icons/lc/teensin.jpg"), url("https://dataobj.ecoassetsservice.com/casino-icons/default.jpg")' }}></div>
            <div className="casino-list-name">29Card Baccarat</div>
        </div>
    </div>
    <div className="casino-list-item">
        <div onClick={() => toast.error("Game Locked")} style={{ cursor: 'pointer' }}>
            <div className="casino-list-item-banner" style={{ backgroundImage: 'url("https://dataobj.ecoassetsservice.com/casino-icons/lc/teenmuf.jpg"), url("https://dataobj.ecoassetsservice.com/casino-icons/default.jpg")' }}></div>
            <div className="casino-list-name">Muflis Teenpatti</div>
        </div>
    </div>
    <div className="casino-list-item">
        <div onClick={() => toast.error("Game Locked")} style={{ cursor: 'pointer' }}>
            <div className="casino-list-item-banner" style={{ backgroundImage: 'url("https://dataobj.ecoassetsservice.com/casino-icons/lc/race17.jpg"), url("https://dataobj.ecoassetsservice.com/casino-icons/default.jpg")' }}></div>
            <div className="casino-list-name">Race To 17</div>
        </div>
    </div>
    <div className="casino-list-item">
        <div onClick={() => toast.error("Game Locked")} style={{ cursor: 'pointer' }}>
            <div className="casino-list-item-banner" style={{ backgroundImage: 'url("https://dataobj.ecoassetsservice.com/casino-icons/lc/teen20b.jpg"), url("https://dataobj.ecoassetsservice.com/casino-icons/default.jpg")' }}></div>
            <div className="casino-list-name">20-20 Teenpatti B</div>
        </div>
    </div>
    <div className="casino-list-item">
        <div onClick={() => toast.error("Game Locked")} style={{ cursor: 'pointer' }}>
            <div className="casino-list-item-banner" style={{ backgroundImage: 'url("https://dataobj.ecoassetsservice.com/casino-icons/lc/trio.jpg"), url("https://dataobj.ecoassetsservice.com/casino-icons/default.jpg")' }}></div>
            <div className="casino-list-name">Trio</div>
        </div>
    </div>
    <div className="casino-list-item">
        <div onClick={() => toast.error("Game Locked")} style={{ cursor: 'pointer' }}>
            <div className="casino-list-item-banner" style={{ backgroundImage: 'url("https://dataobj.ecoassetsservice.com/casino-icons/lc/notenum.jpg"), url("https://dataobj.ecoassetsservice.com/casino-icons/default.jpg")' }}></div>
            <div className="casino-list-name">Note Number</div>
        </div>
    </div>
    <div className="casino-list-item">
        <div onClick={() => toast.error("Game Locked")} style={{ cursor: 'pointer' }}>
            <div className="casino-list-item-banner" style={{ backgroundImage: 'url("https://dataobj.ecoassetsservice.com/casino-icons/lc/teen120.jpg"), url("https://dataobj.ecoassetsservice.com/casino-icons/default.jpg")' }}></div>
            <div className="casino-list-name">1 CARD 20-20</div>
        </div>
    </div>
    <div className="casino-list-item">
        <div onClick={() => toast.error("Game Locked")} style={{ cursor: 'pointer' }}>
            <div className="casino-list-item-banner" style={{ backgroundImage: 'url("https://dataobj.ecoassetsservice.com/casino-icons/lc/teen1.jpg"), url("https://dataobj.ecoassetsservice.com/casino-icons/default.jpg")' }}></div>
            <div className="casino-list-name">1 CARD ONE-DAY</div>
        </div>
    </div>
    <div className="casino-list-item">
        <div onClick={() => toast.error("Game Locked")} style={{ cursor: 'pointer' }}>
            <div className="casino-list-item-banner" style={{ backgroundImage: 'url("https://dataobj.ecoassetsservice.com/casino-icons/lc/ab3.jpg"), url("https://dataobj.ecoassetsservice.com/casino-icons/default.jpg")' }}></div>
            <div className="casino-list-name">ANDAR BAHAR 50 cards</div>
        </div>
    </div>
    <div className="casino-list-item">
        <div onClick={() => toast.error("Game Locked")} style={{ cursor: 'pointer' }}>
            <div className="casino-list-item-banner" style={{ backgroundImage: 'url("https://dataobj.ecoassetsservice.com/casino-icons/lc/aaa2.jpg"), url("https://dataobj.ecoassetsservice.com/casino-icons/default.jpg")' }}></div>
            <div className="casino-list-name">Amar Akbar Anthony 2</div>
        </div>
    </div>
    <div className="casino-list-item">
        <div onClick={() => toast.error("Game Locked")} style={{ cursor: 'pointer' }}>
            <div className="casino-list-item-banner" style={{ backgroundImage: 'url("https://dataobj.ecoassetsservice.com/casino-icons/lc/race2.jpg"), url("https://dataobj.ecoassetsservice.com/casino-icons/default.jpg")' }}></div>
            <div className="casino-list-name">Race to 2nd</div>
        </div>
    </div>
    <div className="casino-list-item">
        <div onClick={() => toast.error("Game Locked")} style={{ cursor: 'pointer' }}>
            <div className="casino-list-item-banner" style={{ backgroundImage: 'url("https://dataobj.ecoassetsservice.com/casino-icons/lc/teen3.jpg"), url("https://dataobj.ecoassetsservice.com/casino-icons/default.jpg")' }}></div>
            <div className="casino-list-name">Instant Teenpatti</div>
        </div>
    </div>
    <div className="casino-list-item">
        <div onClick={() => toast.error("Game Locked")} style={{ cursor: 'pointer' }}>
            <div className="casino-list-item-banner" style={{ backgroundImage: 'url("https://dataobj.ecoassetsservice.com/casino-icons/lc/dum10.jpg"), url("https://dataobj.ecoassetsservice.com/casino-icons/default.jpg")' }}></div>
            <div className="casino-list-name">Dus ka Dum</div>
        </div>
    </div>
    <div className="casino-list-item">
        <div onClick={() => toast.error("Game Locked")} style={{ cursor: 'pointer' }}>
            <div className="casino-list-item-banner" style={{ backgroundImage: 'url("https://dataobj.ecoassetsservice.com/casino-icons/lc/cmeter1.jpg"), url("https://dataobj.ecoassetsservice.com/casino-icons/default.jpg")' }}></div>
            <div className="casino-list-name">1 Card Meter</div>
        </div>
    </div>
    </div>
              

            </div>
        </div>

<section className="footer app-footer">
                
              
                <div className="footer-top">
                    <div className="footer-links">
                        <nav className="navbar navbar-expand-sm">
                            <ul className="navbar-nav">
                                <li className="nav-item">
                                    <a className="nav-link" href="/terms-and-conditions" target="_blank">
                                        Terms and Conditions
                                    </a>
                                </li>
                                <li className="nav-item">
                                    <a className="nav-link" href="/responsible-gaming" target="_blank">
                                        Responsible Gaming
                                    </a>
                                </li>
                            </ul>
                        </nav>
                    </div>
                    <div className="support-detail">
                        <h2>24X7 Support</h2>
                    </div>
                    <div className="social-icons-box"></div>
                </div>

                {/* 2. MIDDLE ROW: SSL Badge + Icons */}
                <div className="footer-bottom">
                    <div className="secure-logo">
                        <div>
                            <img src="https://wver.sprintstaticdata.com/v3/static/front/img/ssl.png" alt="SSL Secure" />
                        </div>
                        <div className="ml-2">
                            <b>100% SAFE</b>
                            <div>Protected connection and encrypted data.</div>
                        </div>
                    </div>
                    <div className="d-inline-block">
                        <button className="btn p-0">
                            <img src="https://versionobj.ecoassetsservice.com/v105/static/front/img/18plus.png" alt="18+" />
                        </button>
                        <a href="https://www.gamcare.org.uk/" target="_blank">
                            <img src="https://versionobj.ecoassetsservice.com/v105/static/front/img/gamecare.png" alt="Gamecare" />
                        </a>
                        <a href="https://www.gamblingtherapy.org/" target="_blank">
                            <img src="https://versionobj.ecoassetsservice.com/v105/static/front/img/gt.png" alt="GT" />
                        </a>
                    </div>
                </div>

                {/* 3. BOTTOM ROW: Copyright Text */}
                <div className="footer-text">
                    <p className="text-centerx">
                        © Copyright {new Date().getFullYear()}. All Rights Reserved. Powered by WAZIRBOOK.
                    </p>
                </div>
            </section>
    </div>

    

    );
};

export default UserDashboard;